import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';

// Lazy-loaded mermaid module (loaded on first diagram render)
let mermaidModule = null;
let mermaidInitialized = false;

async function loadMermaid() {
  if (!mermaidModule) {
    mermaidModule = (await import('mermaid')).default;
  }
  return mermaidModule;
}

const initializeMermaid = (mermaidInstance, theme) => {
  const isDark = theme.palette.mode === 'dark';

  try {
    // Suppress Mermaid's default error UI (prevents DOM pollution)
    mermaidInstance.parseError = () => {};

    mermaidInstance.initialize({
      startOnLoad: false,
      theme: isDark ? 'dark' : 'default',
      themeVariables: {
        // Text and fonts - LARGE for readability
        fontSize: '24px',
        fontFamily: theme.typography.fontFamily,
        textColor: theme.palette.text.primary,

        // Primary colors (main nodes)
        primaryColor: isDark ? '#4f8fba' : '#4a90d9',
        primaryTextColor: isDark ? '#ffffff' : '#ffffff',
        primaryBorderColor: isDark ? '#5a9fcf' : '#3a7bc8',

        // Secondary colors (alternate nodes)
        secondaryColor: isDark ? '#6b5b95' : '#7c6dab',
        secondaryTextColor: isDark ? '#ffffff' : '#ffffff',
        secondaryBorderColor: isDark ? '#7b6ba5' : '#6c5d9b',

        // Tertiary colors (third level)
        tertiaryColor: isDark ? '#3d8b6e' : '#4a9f7e',
        tertiaryTextColor: isDark ? '#ffffff' : '#ffffff',
        tertiaryBorderColor: isDark ? '#4d9b7e' : '#3a8f6e',

        // Background and lines
        background: theme.palette.background.default,
        mainBkg: isDark ? '#4f8fba' : '#4a90d9',
        lineColor: isDark ? '#8899aa' : '#666666',

        // Node specific
        nodeBorder: isDark ? '#5a9fcf' : '#3a7bc8',
        nodeTextColor: isDark ? '#ffffff' : '#ffffff',

        // Flowchart specific
        clusterBkg: isDark ? '#2d3748' : '#e8f4fc',
        clusterBorder: isDark ? '#4a5568' : '#b3d4f0',

        // Edge labels
        edgeLabelBackground: isDark ? '#1a202c' : '#ffffff',

        // Notes and special elements
        noteBkgColor: isDark ? '#4a5568' : '#fff5b3',
        noteTextColor: isDark ? '#e2e8f0' : '#333333',
        noteBorderColor: isDark ? '#718096' : '#e6d98c'
      },
      flowchart: {
        curve: 'basis',
        padding: 20,
        nodeSpacing: 50,
        rankSpacing: 60,
        htmlLabels: true,
        useMaxWidth: true
      },
      securityLevel: 'strict',
      logLevel: 'error'
    });
    mermaidInitialized = true;
  } catch (err) {
    console.error('[MermaidDiagram] Initialization error:', err);
  }
};

/**
 * MermaidDiagram - Robust async Mermaid diagram renderer
 *
 * Features:
 * - Async rendering with proper cleanup
 * - Error handling with retry mechanism
 * - Loading states
 * - Theme adaptation (light/dark)
 * - Responsive SVG scaling
 * - Copy-to-clipboard for diagram code
 * - Accessibility support
 */
const MermaidDiagram = ({ code }) => {
  const theme = useTheme();
  const colors = theme.chat;
  const [svg, setSvg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Used to trigger re-renders on retry
  const renderIdRef = useRef(0);
  const diagramRef = useRef(null);
  // ✅ FIX: Track timeout for cleanup to prevent setState after unmount
  const copyTimeoutRef = useRef(null);
  const copyImageTimeoutRef = useRef(null);

  // Render diagram
  useEffect(() => {
    let isMounted = true;
    const currentRenderId = ++renderIdRef.current;

    const renderDiagram = async () => {
      // Validate input
      if (!code || typeof code !== 'string') {
        if (isMounted) {
          setError('Invalid diagram code');
          setLoading(false);
        }
        return;
      }

      const trimmedCode = code.trim();
      if (!trimmedCode) {
        if (isMounted) {
          setError('Empty diagram code');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load mermaid dynamically (first diagram loads the library)
        const mermaid = await loadMermaid();

        // Initialize Mermaid if not already done or if theme changed
        if (!mermaidInitialized) {
          initializeMermaid(mermaid, theme);
        }

        // Pre-validate syntax with parse() - throws with detailed error message
        // (parseError handler is set to no-op, so no DOM pollution)
        try {
          await mermaid.parse(trimmedCode);
        } catch (parseErr) {
          // Syntax error - throw with detailed message for fallback display
          throw new Error(parseErr.message || 'Invalid Mermaid syntax');
        }

        // Generate unique ID
        const id = `mermaid-diagram-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Render with timeout (only if parse succeeded)
        const timeoutMs = 10000;
        const renderPromise = mermaid.render(id, trimmedCode);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Render timeout (${timeoutMs}ms)`)), timeoutMs)
        );

        const result = await Promise.race([renderPromise, timeoutPromise]);

        // Only update if component still mounted and this is latest render
        if (isMounted && currentRenderId === renderIdRef.current) {
          if (result && result.svg) {
            setSvg(result.svg);
            setLoading(false);
            setError(null);
          } else {
            throw new Error('Invalid render result');
          }
        }
      } catch (err) {
        console.error('[MermaidDiagram] Render error:', err);

        if (isMounted && currentRenderId === renderIdRef.current) {
          const errorMessage = err.message || 'Failed to render diagram';
          setError(errorMessage);
          setLoading(false);
          setSvg(null);
        }
      }
    };

    renderDiagram();

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [code, theme, retryCount]); // Re-render when code, theme, or retry triggers

  // Reinitialize Mermaid when theme changes
  useEffect(() => {
    if (mermaidInitialized && mermaidModule) {
      initializeMermaid(mermaidModule, theme);
    }
  }, [theme.palette.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: theme is stable from useTheme, only mode changes matter for Mermaid re-init

  // ✅ FIX: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      if (copyImageTimeoutRef.current) {
        clearTimeout(copyImageTimeoutRef.current);
      }
    };
  }, []);

  // Copy diagram code to clipboard
  // ✅ FIX: Wrap in useCallback and add timeout cleanup
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('[MermaidDiagram] Copy failed:', err);
    }
  }, [code]);

  // Copy diagram as PNG image to clipboard
  const handleCopyImage = useCallback(async () => {
    if (!diagramRef.current || !svg) return;

    try {
      const svgElement = diagramRef.current.querySelector('svg');
      if (!svgElement) return;

      // Get SVG dimensions
      const bbox = svgElement.getBoundingClientRect();
      const width = bbox.width * 2; // 2x for better quality
      const height = bbox.height * 2;

      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Fill with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Draw SVG to canvas
      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);

        // Convert canvas to blob and copy to clipboard
        canvas.toBlob(async (blob) => {
          if (blob) {
            try {
              await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
              ]);
              setCopiedImage(true);
              if (copyImageTimeoutRef.current) {
                clearTimeout(copyImageTimeoutRef.current);
              }
              copyImageTimeoutRef.current = setTimeout(() => {
                setCopiedImage(false);
                copyImageTimeoutRef.current = null;
              }, 2000);
            } catch (clipboardErr) {
              console.error('[MermaidDiagram] Clipboard write failed:', clipboardErr);
            }
          }
        }, 'image/png');
      };
      img.src = url;
    } catch (err) {
      console.error('[MermaidDiagram] Copy image failed:', err);
    }
  }, [svg]);

  // Retry rendering - triggers useEffect via retryCount dependency
  const handleRetry = () => {
    setSvg(null);
    setError(null);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };

  // Loading state
  if (loading && !svg) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 150,
          bgcolor: colors.markdown.codeBlock.background,
          borderRadius: 1,
          p: 3,
          gap: 2,
          my: 1
        }}
        role="status"
        aria-live="polite"
        aria-label="Loading diagram"
      >
        <CircularProgress size={32} sx={{ color: colors.markdown.codeBlock.text }} />
        <Box sx={{ color: colors.markdown.codeBlock.text, opacity: 0.7, fontSize: '0.875rem' }}>
          Rendering diagram...
        </Box>
      </Box>
    );
  }

  // Error state: fallback to code block display
  if (error) {
    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: 1,
          bgcolor: colors.markdown.codeBlock.background,
          my: 1,
          overflow: 'hidden'
        }}
      >
        {/* Warning banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.75,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.1)',
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.2)'}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="span"
              sx={{
                fontSize: '0.75rem',
                color: theme.palette.warning.main,
                fontWeight: 500
              }}
            >
              ⚠ Mermaid syntax error
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: '0.7rem',
                color: colors.markdown.codeBlock.text,
                opacity: 0.7
              }}
            >
              — {error.length > 60 ? error.substring(0, 60) + '...' : error}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Retry" arrow placement="top">
              <IconButton
                size="small"
                onClick={handleRetry}
                sx={{
                  color: colors.markdown.codeBlock.text,
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? 'Copied!' : 'Copy code'} arrow placement="top">
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  color: colors.markdown.codeBlock.text,
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Code fallback */}
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            overflow: 'auto',
            fontSize: '0.8125rem',
            fontFamily: 'monospace',
            color: colors.markdown.codeBlock.text,
            lineHeight: 1.5,
            '& code': {
              fontFamily: 'inherit'
            }
          }}
        >
          <code>{code}</code>
        </Box>
      </Box>
    );
  }

  // Success state: rendered diagram
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1,
        bgcolor: colors.markdown.codeBlock.background,
        p: 2,
        my: 1,
        overflow: 'auto',
        '&:hover .mermaid-actions': {
          opacity: 1
        }
      }}
    >
      {/* Action buttons */}
      <Box
        className="mermaid-actions"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          zIndex: 10,
          '@media (hover: none)': {
            opacity: 0.7
          }
        }}
      >
        <Tooltip title={copiedImage ? 'Copied!' : 'Copy as image'} arrow placement="left">
          <IconButton
            onClick={handleCopyImage}
            size="small"
            sx={{
              bgcolor: `${colors.markdown.codeBlock.background}cc`,
              backdropFilter: 'blur(4px)',
              color: colors.markdown.codeBlock.text,
              '&:hover': {
                bgcolor: colors.markdown.codeBlock.text,
                color: colors.markdown.codeBlock.background
              }
            }}
            aria-label="Copy diagram as image"
          >
            {copiedImage ? <CheckIcon fontSize="small" /> : <ImageIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={copied ? 'Copied!' : 'Copy code'} arrow placement="left">
          <IconButton
            onClick={handleCopy}
            size="small"
            sx={{
              bgcolor: `${colors.markdown.codeBlock.background}cc`,
              backdropFilter: 'blur(4px)',
              color: colors.markdown.codeBlock.text,
              '&:hover': {
                bgcolor: colors.markdown.codeBlock.text,
                color: colors.markdown.codeBlock.background
              }
            }}
            aria-label="Copy diagram code to clipboard"
          >
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Rendered SVG diagram */}
      <Box
        ref={diagramRef}
        className="mermaid-diagram"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 100,
          '& svg': {
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            minWidth: 'min(400px, 100%)'
          },
          // Force larger text in Mermaid diagrams
          '& .nodeLabel, & .node text, & .label text': {
            fontSize: '16px !important',
            fontWeight: 500
          },
          '& .edgeLabel, & .edgeLabel text': {
            fontSize: '14px !important'
          },
          '& text': {
            fontSize: '14px'
          }
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
        role="img"
        aria-label="Mermaid diagram"
      />
    </Box>
  );
};

MermaidDiagram.propTypes = {
  code: PropTypes.string.isRequired
};

export default MermaidDiagram;
